<template>
  <collapse
    :value="!deleted"
    flexible-height
    name="editcoupon"
    ref="editCouponRef"
  >
    <div class="white_background">
      <carousel
        :paginationSize="6"
        :paginationPadding="6"
        :perPage="1"
        :centerMode="true"
        :speed="200"
        :navigate-to="carouselIndex"
      >
        <slide
          v-for="n in 3"
          :key="n"
        >
          <edit-image
            v-model="coupon.images[n - 1].url"
            @start-save="isSaving = true"
            :callback="createSetUrlToCouponCallback(n - 1)"
            :placeholder="$CONSTANTS.couponImagePlaceholderUrl"
            image-class="coupon_image"
            :width="cardWidth"
            :height="cardWidth / (16 / 9)"
          />
        </slide>
      </carousel>
    </div>

    <form class="flex column pb2">
    <!--  title, description, prices    -->
      <div class="px2 flex column white_background">
      <input
        type="text"
        class="box"
        style="text-align: left; margin-bottom: 6px;"
        v-model="coupon.title"
        name="coupontitle"
        :placeholder="$t('cms.coupon.TitleInput')"
      />
      <hr />
      <label
        class="box html_input mb2"
        :class="{is_valid: !!coupon.text}"
        style="background: rgba(0, 0, 0, 0.02);"
      >
        <vue-editor
          id="coupon-description"
          name="html"
          :placeholder="$t('cms.coupon.DescriptionInput')"
          v-model="coupon.text"
        />
      </label>

      <label class="two_children_box">
        <span>{{ $t('cms.coupon.NormalPriceInput') }}</span>
        <input
          type="text"
          inputmode="decimal"
          :placeholder="currencySymbol"
          v-model="normalPrice"
          @blur="normalPrice = toPrice(normalPrice, currencySymbol)"
          name="normalPrice"
          :class="{cross: !!(coupon.discountPercentage || coupon.newPrice)}"
        />
      </label>

      <label class="two_children_box">
        <span>{{ $t('cms.coupon.NewPriceInput') }}</span>
        <input
          type="text"
          inputmode="decimal"
          :disabled="!isNil(coupon.discountPercentage)"
          :placeholder="currencySymbol"
          v-model="newPrice"
          @blur="newPrice = toPrice(newPrice, currencySymbol)"
          name="newPrice"
          :class="{good: !coupon.discountPercentage}"
        />
      </label>

      <label class="two_children_box">
        <span>{{ $t('cms.coupon.DiscountPercentageInput')}}</span>
        <input
          type="text"
          inputmode="decimal"
          :disabled="!isNil(newPrice)"
          v-validate="validators.PERCENT"
          @input="coupon.discountPercentage = toNumber($event.target.value)"
          :value="isNil(coupon.discountPercentage) ? '' : `${coupon.discountPercentage}%`"
          placeholder="%"
          name="discountPercentage"
          :class="{good: !coupon.newPrice}"
        />
      </label>
      </div>

      <!--   coupon type   -->
      <div class="slightly_yellow_background mt2 flex column px2">
        <icon :iconName="getIconByCouponType()" xlarge class="maa"/>
        <h3 class="heading mt0">{{ $t('cms.coupon.CtaHeader') }}</h3>
        <p class="subheading" style="margin-top: 0">{{ $t('cms.coupon.CtaSubheader') }}</p>
        <select-dropdown
          class="mb2"
          :options="Object.values($CONSTANTS.couponCtaTypes)"
          v-model="coupon.cta"
        />
        <coupon-cta-form v-model="coupon" class="mb2"/>
        <p class="subheading">{{ ctaTypeDescription }}</p>
      </div>


      <!--   Partnership     -->
      <div
        class="mb2 pt2 flex column px2 edit_coupon_blue_bg"
      >
        <icon agreement xlarge class="mxa"/>
        <div class="flex justify_center" style="align-items: baseline;">
          <icon blue-blue-tick :size="14" class="mr1"></icon>
          <h3 class="heading my0">{{ $t('cms.coupon.PartnershipDealTitle') }}</h3>
        </div>
        <p class="subheading">{{ $t('cms.coupon.PartnershipDealText') }}</p>

        <input
          type="text"
          v-model.trim="coupon.partnerName"
          :placeholder="$t('cms.coupon.PartnerNamePlaceholder')"
          class="box"
          style="text-align: left;"
          name="partnerName"
        />

        <input
          type="text"
          v-model.trim="coupon.partnerAddress"
          :placeholder="$t('cms.coupon.PartnerAddressPlaceholder')"
          class="box"
          style="text-align: left;"
          name="partnerAddress"
        />

        <input
          type="text"
          v-model.trim="coupon.partnerPostCode"
          :placeholder="$t('cms.coupon.PartnerPostCodePlaceholder')"
          class="box"
          style="text-align: left;"
          name="partnerName"
        />

        <hr class="mx0 mb0"/>
      </div>

      <button
        type="button"
        name="savecoupon"
        class="box primary mx2"
        :disabled="disabledSave"
        @click="save()"
      >{{ $t('cms.coupon.SaveButton') }}</button>

      <delete
        v-if="!isNew"
        small
        class="mx2"
        @delete="deleteCoupon()"
        :text="$t('cms.view.DeleteCouponButton')"
      />
    </form>
  </collapse>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';

import store from '@/store';
import Delete from '@/components/Delete.vue';
import EditImage from '@/components/EditImage.vue';
import CarouselMixin from '@/mixins/carousel_mixin';
import { toNumber, toPrice } from '@/utils/numbers';
import { isIdGenerated, isIdNew } from '@/utils/id_utils';
import { saveCoupon, uploadImage } from '@/utils/save_utils';
import { captureException } from '@/plugins/debugging';
import { objectIsDifferent } from '@/utils/difference';
import SelectDropdown from '@/components/SelectDropdown.vue';
import CouponCtaForm from '@/components/cta/CouponCtaForm.vue';
import CONSTANTS from '@/constants/constants';
import Icon from '@/components/global/Icon.vue';

export default {
  name: 'edit-coupon',

  mixins: [CarouselMixin],

  components: {
    SelectDropdown,
    EditImage,
    Delete,
    CouponCtaForm,
    VueEditor: () => import('vue2-editor').then(({ VueEditor }) => VueEditor),
    Icon,
  },

  props: {
    value: {
      type: Object,
      required: true,
      validator(value) {
        const hasImageArray = value.images && Number.isInteger(value.images.length);

        if (!hasImageArray) {
          throw Error('input didnt contain "images" array');
        }

        return hasImageArray;
      },
    },
  },

  data() {
    return {
      coupon: cloneDeep(this.value || this.$MODELS.coupon),
      savedCoupon: {},
      deleted: false,
      normalPrice: null,
      newPrice: null,
      isSaving: false,
      campaignId: null,
      MAX_AMOUNT_OF_IMAGES: 3,
      validators: {
        PERCENT: { regex: /^\d+(\.\d{1,5})?%?$/ },
      },
      cardWidth: 0,
    };
  },

  created() {
    this.setCouponImageObjects();
    this.newPrice = toPrice(this.coupon.newPrice, this.currencySymbol);
    this.normalPrice = toPrice(this.coupon.normalPrice, this.currencySymbol);

    // if we dont store it in data, it will be null in 'beforeDestroy'
    this.campaignId = this.$route.params.campaignId;
    this.setSavedCoupon();

    if (this.isNew) {
      // We have one new coupon in the app.
      // If its this one, delete it on 'delete-new-coupon'
      this.$bus.$on('delete-new-coupon', this.deleteCoupon);
    }
  },

  updated() {
    // on mounted the clientWidth is still 0
    this.cardWidth = this.$refs.editCouponRef.$el.clientWidth;
  },

  beforeDestroy() {
    if (!this.isNew && !this.deleted) {
      store.updateCoupon(this.campaignId, this.coupon);
    }
  },

  computed: {
    ctaTypeDescription() {
      const isCta = ({ value }) => value === this.coupon.cta;
      const cta = Object.values(CONSTANTS.couponCtaTypes).find(isCta)
        || CONSTANTS.couponCtaTypes.redeem;
      return cta.description;
    },

    disabledSave() {
      const isValid = this.containsImages && this.isComplete;
      return this.isSaving || !isValid || !(this.hasChanged || this.isNew);
    },

    hasChanged() {
      return objectIsDifferent(omit(this.coupon, 'id'), omit(this.savedCoupon, 'id'))
        || toNumber(this.normalPrice) !== this.savedCoupon.normalPrice
        || toNumber(this.newPrice) !== this.savedCoupon.newPrice;
    },

    currencySymbol() {
      return store.state.organization.currencySymbol;
    },

    containsImages() {
      return this.coupon.images.filter(({ id }) => !!id).length > 0;
    },

    isComplete() {
      return this.coupon.text && this.coupon.title;
    },

    isNew() {
      return isIdNew(this.coupon.id);
    },
  },

  watch: {
    coupon: {
      deep: true,
      handler() {
        this.setCouponImageObjects();

        // Don't save automatically if coupon is new
        if (this.isNew) { return; }

        // if coupon is not new, make sure we dont respond
        // to 'delete-new-coupon'
        this.$bus.$off('delete-new-coupon', this.deleteCoupon);
      },
    },
  },

  methods: {
    isNil,
    toNumber,
    toPrice,

    getIconByCouponType() {
      const typeKey = Object.keys(CONSTANTS.couponCtaTypes).find(
        key => CONSTANTS.couponCtaTypes[key].value === this.coupon.cta,
      );
      return CONSTANTS.couponCtaTypes[typeKey].icon;
    },

    setCouponImageObjects() {
      if (this.coupon.images.length < 3) {
        this.coupon.images = [
          this.coupon.images[0] || {
            url: null,
            id: null,
          },
          this.coupon.images[1] || {
            url: null,
            id: null,
          },
          this.coupon.images[2] || {
            url: null,
            id: null,
          },
        ];
      }
    },

    setSavedCoupon() {
      this.savedCoupon = cloneDeep(this.coupon);
    },

    createSetUrlToCouponCallback(imageIndex) {
      const orgId = store && store.state && store.state.organization && store.state.organization.id;

      if (!orgId) {
        captureException(new Error(`createSetUrlToCouponCallback was called with falsy store.state.organization.id, ${store.state.organization.id}`));
      }

      return async (url) => {
        this.coupon.images[imageIndex].id = await uploadImage(url, orgId);
        this.isSaving = false;

        // Only save if coupon already exists in DB, otherwise we will save when creating the coupon
        if (!isIdGenerated(this.coupon.id)) {
          // Save images only
          // (so we don't have to worry about overwriting or validating other fields)
          const couponInput = {
            id: this.coupon.id,
            // Save all images so we keep the same order
            images: this.coupon.images.map(img => img.id).filter(v => !!v),
          };
          const { errors } = this.$mutate(this.$mutations.updateCoupon, { couponInput });
          this.handleOptionalErrors(errors);
        }
      };
    },

    async save() {
      // Check for validity
      if (!(this.isComplete && await this.$validator.validate())) {
        this.showErrorNotification(this.$t('cms.coupons.cta.IncompleteCouponError'));
        return;
      }

      const isQRCode = this.coupon.cta === CONSTANTS.couponCtaTypes.qrCode.value;
      if (isQRCode && !this.coupon.qrCode) {
        this.showErrorNotification(this.$t('cms.coupons.cta.MissingQRCodeError'));
        return;
      }

      const isBarcode = this.coupon.cta === CONSTANTS.couponCtaTypes.barcode.value;
      if (isBarcode && !this.coupon.barcode) {
        this.showErrorNotification(this.$t('cms.coupons.cta.InvalidBarCodeError'));
        return;
      }

      // Quill (so vue2editor) sometimes places a span with a non printing char
      // The front end always overwrites this char when it receives it from the back end
      // This causes the save button to always be active, so this span should be removed.
      this.coupon.text = this.coupon.text.replace(/<span class="ql-cursor">.*?<\/span>/, '');

      this.coupon.partnerName = this.coupon.partnerName || '';
      this.coupon.partnerAddress = this.coupon.partnerAddress || '';
      this.coupon.partnerPostCode = this.coupon.partnerPostCode || '';
      this.coupon.normalPrice = toNumber(this.normalPrice);
      this.coupon.newPrice = toNumber(this.newPrice);

      this.isSaving = true; // disable save button
      const { coupon, errors } = await saveCoupon(this.coupon, this.campaignId, this.isNew);

      if (!coupon || errors.length > 0) {
        this.handleErrors(errors);
      }

      if (coupon) {
        // update this.isNew by setting this.coupon.id
        if (this.isNew) {
          const oldId = this.coupon.id;
          this.coupon.id = coupon.id;
          this.setCouponImageObjects();
          store.updateCoupon(this.campaignId, this.coupon, oldId);
        }

        this.$emit('input', cloneDeep(this.coupon));
      }

      this.isSaving = false;
      this.setSavedCoupon();
    },

    async deleteCoupon() {
      this.deleted = true;
      store.deleteCoupon(this.campaignId, this.coupon);
      if (!isIdGenerated(this.coupon.id)) {
        const { id } = this.coupon;
        const { errors } = await this.$mutate(this.$mutations.deleteCoupon, { id });
        this.handleOptionalErrors(errors);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .cross {
    text-decoration: line-through;
  }

  .html_input {
    height: $height_coupon_edit_text_field;
    padding-top: $base_spacing * 2;
    padding-bottom: $base_spacing * 2;
  }

  .radio_container {
    display: flex;
    justify-content: space-evenly;

  }

  .radio_label {
    display: flex;
    align-items: center;
  }

  input[type=radio] {
    margin-right: 7px;
    margin-bottom: 3px;
  }
  input[type=text] {
    margin-bottom: $base_spacing * 2;
  }
  .edit_coupon_blue_bg {
    background: rgba(4, 143, 250, 0.05) // design says: #049ffa, 5% opacity
  }
</style>

<!-- If this is added to the style above, the placeholder styling doesn't work-->
<style lang="scss">
  #coupon-description .ql-editor::before {
    left: 0;
    right: 0;
    font-weight: 400;
    font-size: 14px;
    font-style: normal;
  }
</style>
